/**
 * Currency formatting — driven by Admin → Regional Settings (synced on app load).
 * Always uses currencySymbol, currencySymbolBefore, currencyDecimals, localeTag —
 * never Intl currency-style (which ignores custom symbol/placement).
 */

import { getRegionalDefaults } from './regionalDefaults';

let currencyState = {
  code: process.env.REACT_APP_CURRENCY || '',
  locale: process.env.REACT_APP_CURRENCY_LOCALE || 'en',
  symbol: '',
  decimals: 2,
  symbolBefore: true,
};

export function syncRegionalCurrency(settings) {
  if (!settings) return;
  if (settings.currencyCode) {
    currencyState.code = settings.currencyCode;
  }
  if (settings.localeTag) {
    currencyState.locale = settings.localeTag;
  }
  if (settings.currencySymbol != null) {
    currencyState.symbol = settings.currencySymbol;
  }
  if (settings.currencyDecimals != null) {
    currencyState.decimals = Number(settings.currencyDecimals);
  }
  if (settings.currencySymbolBefore != null) {
    currencyState.symbolBefore = settings.currencySymbolBefore;
  }
}

export const CURRENCY = currencyState.code;

/** Active currency symbol from regional_settings (synced at runtime). */
export function getCurrencySymbol() {
  const d = getRegionalDefaults();
  return d.currencySymbol || currencyState.symbol || '';
}

/** Whether the symbol is shown before the amount. */
export function isCurrencySymbolBefore() {
  const d = getRegionalDefaults();
  if (d.currencySymbolBefore != null) return Boolean(d.currencySymbolBefore);
  return currencyState.symbolBefore;
}

/** Active decimal places from regional_settings. */
export function getCurrencyDecimals() {
  const d = getRegionalDefaults();
  if (d.currencyDecimals != null) return Number(d.currencyDecimals);
  return currencyState.decimals;
}

/** Format fixed-amount discounts using academy currency. */
export function formatDiscountValue(type, value) {
  if (type === 'PERCENT' || type === 'percent') {
    return `${value}%`;
  }
  return formatCurrency(value);
}

/**
 * Format an amount for end-user display using Regional Settings.
 * Matches backend RegionalSettingsService.formatAmount and useCurrencyFormatter.
 */
export function formatCurrency(amount) {
  if (amount === null || amount === undefined) return '';
  const n = parseFloat(amount);
  const num = isNaN(n) ? 0 : n;
  const d = getRegionalDefaults();
  const decimals = d.currencyDecimals != null ? Number(d.currencyDecimals) : currencyState.decimals;
  const locale = d.localeTag || currencyState.locale || 'en';
  const sym = d.currencySymbol || currencyState.symbol || '';
  const before = d.currencySymbolBefore != null
    ? Boolean(d.currencySymbolBefore)
    : currencyState.symbolBefore;

  let formatted;
  try {
    formatted = new Intl.NumberFormat(locale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(num);
  } catch {
    formatted = num.toFixed(decimals);
  }

  if (!sym) return formatted;
  return before ? `${sym} ${formatted}` : `${formatted} ${sym}`;
}

/** Round to regional decimal places (for gateway amounts / calculations). */
export function formatCurrencyRaw(amount) {
  const decimals = getCurrencyDecimals();
  const n = parseFloat(amount) || 0;
  const factor = 10 ** decimals;
  return Math.round(n * factor) / factor;
}

export function parseCurrencyString(str) {
  if (!str) return 0;
  const cleaned = String(str).replace(/[^\d.-]/g, '').trim();
  return parseFloat(cleaned) || 0;
}

export default formatCurrency;
